const normalizePositiveInteger = (value) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const normalizeGeneration = (value) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

const normalizeRagChatTitle = (value) => String(value || '')
    .replace(/\s+/gu, ' ')
    .replace(/(?:\.\.\.|…)$/u, '')
    .replace(/^[\s"'`“”‘’]+|[\s"'`“”‘’.:;!?…-]+$/gu, '')
    .trim();

const RAG_PLACEHOLDER_CHAT_TITLES = new Set([
    '',
    'new',
    'new chat',
    'new rag chat',
    'new jane chat',
    'new jane agent run',
    'untitled',
    'untitled chat',
]);

export const isRagChatTitlePlaceholder = (value) => (
    RAG_PLACEHOLDER_CHAT_TITLES.has(normalizeRagChatTitle(value).toLowerCase())
);

export const getAcceptedRagChatTitleUpdate = ({ receipt = null, fallbackChatId = 0 } = {}) => {
    if (!receipt || Array.isArray(receipt) || typeof receipt !== 'object') return null;
    const receiptChat = receipt.chat && !Array.isArray(receipt.chat) && typeof receipt.chat === 'object'
        ? receipt.chat
        : null;
    const nestedTitle = String(receiptChat?.title || '').trim();
    const title = nestedTitle || String(receipt.chat_title || '').trim();
    const chatId = normalizePositiveInteger(
        (nestedTitle ? receiptChat?.id : receipt.chat_id) || receipt.chat_id || fallbackChatId
    );
    if (!chatId || isRagChatTitlePlaceholder(title)) return null;
    return { chatId, title };
};

// Admission receipts may arrive after the user has explicitly renamed the
// chat. They can replace a local placeholder, but never a meaningful title.
export const reconcileAcceptedRagChatTitle = (chats, update) => {
    if (!Array.isArray(chats) || !update?.chatId || isRagChatTitlePlaceholder(update?.title)) return chats;
    let changed = false;
    const next = chats.map((chat) => {
        if (Number(chat?.id) !== Number(update.chatId)
            || !isRagChatTitlePlaceholder(chat?.title)
        ) return chat;
        changed = true;
        return { ...chat, title: String(update.title).trim() };
    });
    return changed ? next : chats;
};

export const reconcileAcceptedRagChatTitleValue = (currentTitle, acceptedTitle) => (
    isRagChatTitlePlaceholder(currentTitle) && !isRagChatTitlePlaceholder(acceptedTitle)
        ? String(acceptedTitle).trim()
        : currentTitle
);

export const createRagClientSaveToken = () => {
    const secureCrypto = typeof window !== 'undefined' ? window.crypto : null;
    if (typeof secureCrypto?.randomUUID === 'function') {
        return `save-${secureCrypto.randomUUID()}`;
    }
    if (typeof secureCrypto?.getRandomValues === 'function') {
        const bytes = new Uint8Array(24);
        secureCrypto.getRandomValues(bytes);
        return `save-${Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')}`;
    }
    return `save-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
};

export const isRagRunGenerationCurrent = (runGeneration, currentGeneration) => {
    const expected = normalizeGeneration(runGeneration);
    const current = normalizeGeneration(currentGeneration);
    return expected !== null && current !== null && expected === current;
};

export const shouldAdvanceRagSessionForChatLoad = ({ silent = false, switchingChats = false } = {}) => (
    !Boolean(silent && !switchingChats)
);

// Agent v2 may fail before or after it persists the chat turn. Only the server
// can safely distinguish those cases. Never start the legacy runtime unless
// the response explicitly says the original prompt was not persisted.
export const shouldFallbackFromJaneAgentStart = (response = {}) => (
    response?.fallback_to_legacy === true
    && response?.persisted_message === false
);

const agentV2OperationPattern = /^jane-op-[A-Za-z0-9][A-Za-z0-9._:-]{15,71}$/;
const agentV2RunPattern = /^jane-v2-[a-f0-9]{24}$/;
const agentV2TokenPattern = /^agent-v2-[a-f0-9]{32}$/;
const agentV2Field = (value, key) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor && !Object.prototype.hasOwnProperty.call(descriptor, 'value')) {
        throw new Error('Invalid Jane identity field.');
    }
    return descriptor?.value;
};
const agentV2Integer = (value, allowZero = false) => {
    if (typeof value !== 'number' && !(typeof value === 'string' && /^(0|[1-9][0-9]*)$/.test(value))) return null;
    const number = Number(value);
    return Number.isSafeInteger(number) && number >= (allowZero ? 0 : 1) ? number : null;
};
const agentV2Chat = value => value === null ? 0 : agentV2Integer(value, true);
const agentV2String = (value, pattern) => typeof value === 'string' && pattern.test(value) ? value : '';
const agentV2Tuple = (value) => {
    const runId = agentV2String(agentV2Field(value, 'run_id'), agentV2RunPattern);
    const requestToken = agentV2String(agentV2Field(value, 'request_token'), agentV2TokenPattern);
    const chatId = agentV2Integer(agentV2Field(value, 'chat_id'));
    const userMessageId = agentV2Integer(agentV2Field(value, 'user_message_id'));
    const assistantMessageId = agentV2Integer(agentV2Field(value, 'assistant_message_id'));
    if (!runId || !requestToken || !chatId || !userMessageId || !assistantMessageId || userMessageId === assistantMessageId) return null;
    return { run_id: runId, chat_id: chatId, request_token: requestToken, user_message_id: userMessageId, assistant_message_id: assistantMessageId };
};
const hasAgentV2Marker = (value) => {
    const runId = agentV2Field(value, 'run_id');
    const token = agentV2Field(value, 'request_token');
    const mode = agentV2Field(value, 'mode');
    const operationId = agentV2Field(value, 'client_operation_id');
    return agentV2Field(value, 'runtime') === 'agent_v2'
        || (typeof runId === 'string' && runId.startsWith('jane-v2-'))
        || (typeof token === 'string' && token.startsWith('agent-v2-'))
        || (typeof mode === 'string' && mode.startsWith('jane_agent_v2'))
        || (operationId !== undefined && operationId !== null && operationId !== '');
};

// One ID belongs to one user submission. Callers retain it in memory; this
// helper never retries a request, stores its body, or falls back to weak RNG.
export const createRagAgentV2OperationId = () => {
    try {
        const secureCrypto = typeof window !== 'undefined' ? window.crypto : null;
        if (typeof secureCrypto?.randomUUID === 'function') {
            const uuid = secureCrypto.randomUUID();
            if (typeof uuid === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(uuid)) return `jane-op-${uuid}`;
        }
        if (typeof secureCrypto?.getRandomValues === 'function') {
            const bytes = new Uint8Array(24);
            secureCrypto.getRandomValues(bytes);
            return `jane-op-${Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')}`;
        }
    } catch (error) {
        // Do not expose platform errors or generate a weaker replacement ID.
    }
    const error = new Error('Jane could not securely identify this submission.');
    error.code = 'secure_operation_id_unavailable';
    throw error;
};

export const normalizeRagAgentV2Acknowledgement = ({ receipt, clientOperationId, chatId = 0, actorId } = {}) => {
    try {
        const operationId = agentV2String(clientOperationId, agentV2OperationPattern);
        const expectedChat = agentV2Chat(chatId);
        const tuple = agentV2Tuple(receipt);
        const echoedOperation = agentV2Field(receipt, 'client_operation_id');
        const echoedActor = agentV2Field(receipt, 'user_id');
        if (!operationId || expectedChat === null || !tuple || (expectedChat > 0 && tuple.chat_id !== expectedChat)
            || (echoedOperation !== undefined && echoedOperation !== operationId)
            || (echoedActor !== undefined && (!agentV2Integer(actorId) || agentV2Integer(echoedActor) !== agentV2Integer(actorId)))) return null;
        return Object.freeze({ ...tuple, client_operation_id: operationId });
    } catch (error) { return null; }
};

export const createRagAgentV2RuntimeIntent = ({ runGeneration, actorId, chatId = 0, originChatId = chatId, clientOperationId, acknowledgement = null } = {}) => {
    const generation = agentV2Integer(runGeneration, true);
    const actor = agentV2Integer(actorId);
    const chat = agentV2Chat(chatId);
    const origin = agentV2Chat(originChatId);
    const operationId = agentV2String(clientOperationId, agentV2OperationPattern);
    if (generation === null || !actor || chat === null || origin === null || !operationId || (origin > 0 && chat !== origin)) return null;
    const tuple = acknowledgement === null ? null : normalizeRagAgentV2Acknowledgement({ receipt: acknowledgement, clientOperationId: operationId, chatId: chat, actorId: actor });
    if (acknowledgement !== null && !tuple) return null;
    return Object.freeze({ runtime: 'agent_v2', run_generation: generation, actor_id: actor, origin_chat_id: origin,
        chat_id: tuple?.chat_id ?? chat, client_operation_id: operationId, acknowledgement: tuple, phase: tuple ? 'active' : 'starting' });
};

export const isRagRuntimeIntentOwned = (intent, { runGeneration, actorId, chatId } = {}) => {
    try {
        const generation = agentV2Integer(runGeneration, true);
        const actor = agentV2Integer(actorId);
        const chat = agentV2Chat(chatId);
        const origin = agentV2Integer(agentV2Field(intent, 'origin_chat_id'), true);
        const currentChat = agentV2Integer(agentV2Field(intent, 'chat_id'), true);
        return agentV2Field(intent, 'runtime') === 'agent_v2' && generation !== null && actor !== null && chat !== null
            && generation === agentV2Field(intent, 'run_generation') && actor === agentV2Field(intent, 'actor_id')
            && origin !== null && currentChat !== null && (origin === 0 || origin === currentChat)
            && (chat === currentChat || (origin === 0 && chat === 0))
            && Boolean(agentV2String(agentV2Field(intent, 'client_operation_id'), agentV2OperationPattern));
    } catch (error) { return false; }
};

const heldAgentV2Cancel = () => ({ runtime: 'held', endpoint: null, payload: null, reason: 'agent_v2_identity_unresolved' });
const agentV2Cancel = tuple => ({ runtime: 'agent_v2', endpoint: 'jane-agent-actions.php', payload: {
    action: 'cancel', run_id: tuple.run_id, chat_id: tuple.chat_id, request_token: tuple.request_token,
    user_message_id: tuple.user_message_id, assistant_message_id: tuple.assistant_message_id,
} });

export const buildRagCancelTarget = ({ activeRequest = null, runtimeIntent = null, runGeneration, actorId, chatId, fallbackChatId = 0 } = {}) => {
    try {
        if (isRagRuntimeIntentOwned(runtimeIntent, { runGeneration, actorId, chatId })) {
            const tuple = normalizeRagAgentV2Acknowledgement({ receipt: agentV2Field(runtimeIntent, 'acknowledgement'),
                clientOperationId: agentV2Field(runtimeIntent, 'client_operation_id'), chatId: agentV2Field(runtimeIntent, 'chat_id'), actorId });
            return tuple ? agentV2Cancel(tuple) : heldAgentV2Cancel();
        }
        const rawRun = agentV2Field(activeRequest, 'run_id');
        if (hasAgentV2Marker(activeRequest) || (rawRun !== undefined && rawRun !== null && rawRun !== '')) {
            const tuple = agentV2Tuple(activeRequest);
            const actor = agentV2Integer(actorId);
            const currentChat = agentV2Integer(chatId);
            return tuple && actor && currentChat === tuple.chat_id && agentV2Integer(agentV2Field(activeRequest, 'user_id')) === actor
                ? agentV2Cancel(tuple) : heldAgentV2Cancel();
        }
        const token = agentV2Field(activeRequest, 'request_token');
        return { runtime: 'legacy', endpoint: 'library-rag-chat.php', payload: { action: 'cancel_request',
            chat_id: agentV2Integer(agentV2Field(activeRequest, 'chat_id')) || agentV2Integer(fallbackChatId) || null,
            request_token: typeof token === 'string' ? token.trim() : '',
        } };
    } catch (error) { return heldAgentV2Cancel(); }
};

export const getRagTerminalPersistenceAction = ({ response = null, runtimeIntent = null, runGeneration, actorId, chatId } = {}) => {
    try {
        if (agentV2Field(response, 'turn_persisted') === true) return 'reload_persisted';
        if (hasAgentV2Marker(response) || isRagRuntimeIntentOwned(runtimeIntent, { runGeneration, actorId, chatId })) return 'reload_agent_v2_unverified';
        return 'save_legacy';
    } catch (error) { return 'reload_agent_v2_unverified'; }
};

// This validates the server's saved terminal identity, not its answer content
// or digest. An absent active request alone cannot prove a run has settled.
export const isRagAgentV2SnapshotSettled = ({ snapshot, acknowledgement, actorId } = {}) => {
    try {
        const tuple = agentV2Tuple(acknowledgement);
        const actor = agentV2Integer(actorId);
        if (!tuple || !actor || agentV2Field(snapshot, 'success') !== true || agentV2Field(snapshot, 'active_request') !== null
            || agentV2Integer(agentV2Field(agentV2Field(snapshot, 'chat'), 'id')) !== tuple.chat_id) return false;
        const messages = agentV2Field(snapshot, 'messages');
        if (!Array.isArray(messages)) return false;
        const matching = messages.filter(message => agentV2Integer(agentV2Field(message, 'id')) === tuple.assistant_message_id);
        if (matching.length !== 1) return false;
        const message = matching[0];
        if (agentV2Field(message, 'role') !== 'assistant' || agentV2Integer(agentV2Field(message, 'chat_id')) !== tuple.chat_id) return false;
        const messageActor = agentV2Field(message, 'user_id');
        if (messageActor !== undefined && agentV2Integer(messageActor) !== actor) return false;
        const metadata = agentV2Field(message, 'metadata');
        const terminal = agentV2Field(metadata, 'jane_agent_v2');
        const terminalTuple = agentV2Tuple(terminal);
        return agentV2Field(metadata, 'pending_request') === false && agentV2Field(metadata, 'request_token') === tuple.request_token
            && agentV2Field(terminal, 'terminal') === true && agentV2Field(terminal, 'version') === 1
            && agentV2Integer(agentV2Field(terminal, 'user_id')) === actor
            && agentV2Field(terminal, 'generation') === tuple.request_token
            && ['complete', 'failed', 'cancelled'].includes(agentV2Field(terminal, 'status'))
            && Boolean(agentV2String(agentV2Field(terminal, 'revision'), /^terminal-v1-[a-f0-9]{48}$/))
            && Boolean(agentV2String(agentV2Field(terminal, 'payload_sha256'), /^[a-f0-9]{64}$/))
            && terminalTuple !== null && Object.keys(tuple).every(key => terminalTuple[key] === tuple[key]);
    } catch (error) { return false; }
};

export const createRagChatOperationContext = ({ chatId, sessionId, runGeneration } = {}) => ({
    chatId: normalizePositiveInteger(chatId),
    sessionId: normalizeGeneration(sessionId),
    runGeneration: normalizeGeneration(runGeneration),
});

export const isRagChatOperationCurrent = (context = {}, current = {}) => (
    context.chatId !== null
    && context.sessionId !== null
    && context.runGeneration !== null
    && context.chatId === normalizePositiveInteger(current.activeChatId)
    && context.sessionId === normalizeGeneration(current.sessionId)
    && context.runGeneration === normalizeGeneration(current.runGeneration)
);

export const createRagUploadContext = ({ chatId, sessionId, operationId, caseId = 0 } = {}) => ({
    chatId: normalizePositiveInteger(chatId),
    sessionId: normalizeGeneration(sessionId),
    operationId: normalizePositiveInteger(operationId),
    caseId: normalizePositiveInteger(caseId) || 0,
});

export const isRagUploadContextCurrent = (context = {}, current = {}) => (
    context.chatId !== null
    && context.sessionId !== null
    && context.operationId !== null
    && context.chatId === normalizePositiveInteger(current.activeChatId)
    && context.sessionId === normalizeGeneration(current.sessionId)
    && context.operationId === normalizePositiveInteger(current.operationId)
);
